import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Select,
  Skeleton,
  Text,
  TextField,
  Badge,
  SegmentedControl,
  Separator,
  Tooltip,
} from "@radix-ui/themes";
import {
  Search,
  Trash2,
  MapPin,
  Sparkles,
  Minus,
  ArrowUpToLine,
  Star,
  Plus,
  PlusCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  WEATHER_LAYOUTS,
  WEATHER_STYLES,
  type GeocodeResult,
  type WeatherCard,
  type WeatherLayout,
  type WeatherStyle,
} from "@mem/shared";
import {
  useCreateWeatherCard,
  useDeleteWeatherCard,
  useGeocodeCity,
  usePromoteWeatherCard,
  useWeatherCards,
} from "@/hooks/use-weather-cards";
import {
  useWeatherFavorites,
  useAddWeatherFavorite,
  useRemoveWeatherFavorite,
  useAddCardFromFavorite,
  type WeatherFavorite,
} from "@/hooks/use-weather-favorites";
import { PromoteDialog } from "@/components/shared/promote-dialog";
import { isFavorited, findFavoriteId } from "@/lib/weather-favorites";

interface WeatherTabProps {
  slug: string;
}

/**
 * Onglet "Météo" du board.
 *
 * Structure :
 *   1. Section Favoris (villes mémorisées, ajout en 1 clic)
 *   2. Recherche + ajout d'une nouvelle ville
 *   3. Cartes météo actives du jour (avec promote + suppression + étoile)
 */
export function WeatherTab({ slug }: WeatherTabProps) {
  const { t, i18n } = useTranslation();
  const { data: cards, isLoading } = useWeatherCards(slug);
  const { data: favorites = [], isLoading: favsLoading } = useWeatherFavorites(slug);
  const createCard = useCreateWeatherCard(slug);
  const deleteCard = useDeleteWeatherCard(slug);
  const promoteCard = usePromoteWeatherCard(slug);
  const addFavorite = useAddWeatherFavorite(slug);
  const removeFavorite = useRemoveWeatherFavorite(slug);
  const addFromFavorite = useAddCardFromFavorite(slug);
  const geocode = useGeocodeCity();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<GeocodeResult | null>(null);
  const [layout, setLayout] = useState<WeatherLayout>("current_today");
  const [style, setStyle] = useState<WeatherStyle>("warm");

  const results = geocode.data?.ok ? geocode.data.data.results : [];

  function handleSearch() {
    const q = query.trim();
    if (!q) return;
    setSelected(null);
    geocode.mutate({ q, lang: i18n.language });
  }

  function handleAdd() {
    if (!selected) return;
    createCard.mutate(
      {
        city_query: query.trim() || selected.name,
        city_label: cityLabel(selected),
        country_code: selected.country_code,
        latitude: selected.latitude,
        longitude: selected.longitude,
        timezone: selected.timezone,
        layout,
        style,
      },
      {
        onSuccess: (result) => {
          if (result.ok) {
            setQuery("");
            setSelected(null);
          }
        },
      },
    );
  }

  function handleSaveFavorite() {
    if (!selected) return;
    addFavorite.mutate({
      id: crypto.randomUUID(),
      city_label: cityLabel(selected),
      country_code: selected.country_code,
      city_query: query.trim() || selected.name,
      latitude: selected.latitude,
      longitude: selected.longitude,
      timezone: selected.timezone,
      layout,
      style,
    });
  }

  return (
    <Flex direction="column" gap="4">

      {/* ── 1. Favoris ─────────────────────────────────────────── */}
      <FavoritesSection
        favorites={favorites}
        isLoading={favsLoading}
        onAdd={(fav) => addFromFavorite.mutate(fav)}
        onRemove={(id) => removeFavorite.mutate(id)}
        isAddingId={addFromFavorite.isPending ? (addFromFavorite.variables as WeatherFavorite)?.id : undefined}
        isRemovingId={removeFavorite.isPending ? (removeFavorite.variables as string) : undefined}
      />

      {/* ── 2. Recherche + ajout ────────────────────────────────── */}
      <Card>
        <Flex direction="column" gap="3">
          <Text as="label" size="2" weight="medium">
            {t("board.weather_city_label")}
          </Text>
          <Flex gap="2" direction={{ initial: "column", sm: "row" }}>
            <TextField.Root
              placeholder={t("board.weather_city_placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleSearch(); }
              }}
              style={{ flexGrow: 1 }}
            />
            <Button
              onClick={handleSearch}
              disabled={!query.trim() || geocode.isPending}
              style={{ minHeight: 40 }}
            >
              <Search size={16} />
              {geocode.isPending ? t("board.weather_searching") : t("board.weather_search")}
            </Button>
          </Flex>

          {geocode.isPending && (
            <Flex direction="column" gap="2">
              <Skeleton height="48px" />
              <Skeleton height="48px" />
            </Flex>
          )}

          {!geocode.isPending && geocode.data?.ok && results.length === 0 && (
            <Text size="2" color="gray">{t("board.weather_no_results")}</Text>
          )}

          {results.length > 0 && (
            <Flex direction="column" gap="1">
              {results.slice(0, 6).map((r, idx) => {
                const isActive =
                  selected !== null &&
                  selected.latitude === r.latitude &&
                  selected.longitude === r.longitude;
                return (
                  <button
                    key={`${r.latitude}-${r.longitude}-${idx}`}
                    type="button"
                    onClick={() => setSelected(r)}
                    style={{
                      all: "unset",
                      cursor: "pointer",
                      padding: "var(--space-2) var(--space-3)",
                      borderRadius: "var(--radius-2)",
                      border: `1px solid ${isActive ? "var(--accent-9)" : "var(--gray-5)"}`,
                      background: isActive ? "var(--accent-3)" : "transparent",
                    }}
                  >
                    <Flex align="center" gap="2">
                      <MapPin size={14} />
                      <Text size="2" weight={isActive ? "medium" : "regular"}>
                        {cityLabel(r)}
                      </Text>
                    </Flex>
                  </button>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Card>

      {/* Configuration + ajout */}
      {selected && (
        <Card>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">{t("board.weather_layout_label")}</Text>
              <Select.Root value={layout} onValueChange={(v) => setLayout(v as WeatherLayout)}>
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  {WEATHER_LAYOUTS.map((l) => (
                    <Select.Item key={l} value={l}>{t(`weather.layout.${l}`)}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">{t("board.weather_style_label")}</Text>
              <SegmentedControl.Root value={style} onValueChange={(v) => setStyle(v as WeatherStyle)}>
                {WEATHER_STYLES.map((s) => (
                  <SegmentedControl.Item key={s} value={s}>
                    <Flex align="center" gap="1">
                      {s === "warm" ? <Sparkles size={14} /> : <Minus size={14} />}
                      {t(`weather.style.${s}`)}
                    </Flex>
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl.Root>
            </Flex>

            <Flex gap="2">
              <Button
                onClick={handleAdd}
                disabled={createCard.isPending}
                style={{ flex: 1, minHeight: 40 }}
              >
                {t("board.weather_add_button")}
              </Button>
              {/* Mémoriser en favori avec les préférences actuelles */}
              <Tooltip content={t("board.weather_favorite_save_hint")}>
                <IconButton
                  variant="soft"
                  color={isFavorited(favorites, selected.latitude, selected.longitude, layout, style) ? "yellow" : "gray"}
                  size="3"
                  onClick={handleSaveFavorite}
                  disabled={
                    addFavorite.isPending ||
                    isFavorited(favorites, selected.latitude, selected.longitude, layout, style)
                  }
                >
                  <Star
                    size={16}
                    fill={isFavorited(favorites, selected.latitude, selected.longitude, layout, style) ? "currentColor" : "none"}
                  />
                </IconButton>
              </Tooltip>
            </Flex>
          </Flex>
        </Card>
      )}

      {/* ── 3. Cartes actives du jour ───────────────────────────── */}
      <Flex direction="column" gap="2">
        {isLoading && [...Array(2)].map((_, i) => <Skeleton key={i} height="80px" />)}

        {!isLoading && (cards ?? []).length === 0 && (
          <Box py="4" style={{ textAlign: "center" }}>
            <Text color="gray" size="2" as="p">{t("board.weather_cards_empty")}</Text>
          </Box>
        )}

        {(cards ?? []).map((card) => (
          <WeatherCardRow
            key={card.id}
            card={card}
            favorites={favorites}
            onDelete={() => deleteCard.mutate(card.id)}
            onPromote={(layout) => promoteCard.mutate({ cardId: card.id, layout })}
            onToggleFavorite={(fav) => {
              const existingId = findFavoriteId(favorites, card.latitude, card.longitude, card.layout, card.style);
              if (existingId) {
                removeFavorite.mutate(existingId);
              } else {
                addFavorite.mutate(fav);
              }
            }}
            isDeleting={deleteCard.isPending && deleteCard.variables === card.id}
            isPromoting={promoteCard.isPending && (promoteCard.variables as { cardId: string })?.cardId === card.id}
          />
        ))}
      </Flex>
    </Flex>
  );
}

// ─── Section Favoris ──────────────────────────────────────────────

function FavoritesSection({
  favorites,
  isLoading,
  onAdd,
  onRemove,
  isAddingId,
  isRemovingId,
}: {
  favorites: WeatherFavorite[];
  isLoading: boolean;
  onAdd: (fav: WeatherFavorite) => void;
  onRemove: (id: string) => void;
  isAddingId?: string;
  isRemovingId?: string;
}) {
  const { t } = useTranslation();

  if (isLoading) return <Skeleton height="60px" />;

  return (
    <Card variant="surface">
      <Flex direction="column" gap="3">
        {/* En-tête */}
        <Flex align="center" gap="2">
          <Star size={15} fill="currentColor" color="var(--yellow-9)" />
          <Text size="2" weight="medium">{t("board.weather_favorites_title")}</Text>
          {favorites.length > 0 && (
            <Badge color="gray" size="1">{favorites.length}</Badge>
          )}
        </Flex>

        {favorites.length === 0 ? (
          <Text size="2" color="gray">
            {t("board.weather_favorites_empty")}
          </Text>
        ) : (
          <Flex direction="column" gap="2">
            {favorites.map((fav) => (
              <Flex key={fav.id} align="center" gap="2">
                <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
                  <Flex align="center" gap="2">
                    <MapPin size={13} />
                    <Text size="2" weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fav.city_label}
                    </Text>
                  </Flex>
                  <Flex gap="1">
                    <Badge size="1" variant="soft" color="violet">{t(`weather.layout.${fav.layout}`)}</Badge>
                    <Badge size="1" variant="soft" color="gray">{t(`weather.style.${fav.style}`)}</Badge>
                  </Flex>
                </Flex>
                <Tooltip content={t("board.weather_favorite_add_card")}>
                  <IconButton
                    size="2"
                    variant="soft"
                    color="blue"
                    onClick={() => onAdd(fav)}
                    disabled={isAddingId === fav.id}
                    loading={isAddingId === fav.id}
                  >
                    <Plus size={14} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={t("board.weather_favorite_remove")}>
                  <IconButton
                    size="2"
                    variant="ghost"
                    color="red"
                    onClick={() => onRemove(fav.id)}
                    disabled={isRemovingId === fav.id}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </Tooltip>
              </Flex>
            ))}

            {/* Séparateur + "Tout ajouter" si plusieurs favoris */}
            {favorites.length > 1 && (
              <>
                <Separator size="4" />
                <Button
                  variant="soft"
                  size="2"
                  onClick={() => favorites.forEach(onAdd)}
                  disabled={!!isAddingId}
                >
                  <PlusCircle size={14} />
                  {t("board.weather_favorites_add_all")}
                </Button>
              </>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

// ─── Ligne de carte météo existante ──────────────────────────────

function WeatherCardRow({
  card,
  favorites,
  onDelete,
  onPromote,
  onToggleFavorite,
  isDeleting,
  isPromoting,
}: {
  card: WeatherCard;
  favorites: WeatherFavorite[];
  onDelete: () => void;
  onPromote: (layout: WeatherLayout) => void;
  onToggleFavorite: (fav: WeatherFavorite) => void;
  isDeleting?: boolean;
  isPromoting?: boolean;
}) {
  const { t } = useTranslation();
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<WeatherLayout>(card.layout);

  const alreadyFavorite = isFavorited(favorites, card.latitude, card.longitude, card.layout, card.style);

  function handleRowClick() {
    setSelectedLayout(card.layout);
    setPromoteOpen(true);
  }
  function handleRowKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleRowClick();
    }
  }
  function handleConfirm() {
    onPromote(selectedLayout);
    setPromoteOpen(false);
  }

  return (
    <Box>
      <Card
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={handleRowKey}
        title={t("board.weather_promote_hint")}
        aria-label={t("board.weather_promote_aria")}
        style={{ cursor: "pointer" }}
      >
        <Flex justify="between" align="center" gap="2">
          <Flex align="center" gap="2" style={{ flex: 1, minWidth: 0 }}>
            <IconButton
              variant="solid"
              color="violet"
              size="1"
              onClick={(e) => { e.stopPropagation(); handleRowClick(); }}
              disabled={isPromoting}
              aria-label={t("board.weather_promote_aria")}
            >
              <ArrowUpToLine size={12} />
            </IconButton>
            <Flex direction="column" gap="1" style={{ minWidth: 0, flex: 1 }}>
              <Flex align="center" gap="2">
                <MapPin size={14} />
                <Text size="2" weight="medium" style={{ wordBreak: "break-word" }}>
                  {card.city_label}
                  {card.country_code ? ` · ${card.country_code}` : ""}
                </Text>
              </Flex>
              <Flex gap="1" wrap="wrap">
                <Badge variant="soft" color="violet">{t(`weather.layout.${card.layout}`)}</Badge>
                <Badge variant="soft" color="gray">{t(`weather.style.${card.style}`)}</Badge>
              </Flex>
            </Flex>
          </Flex>

          {/* Étoile : mémoriser / oublier */}
          <Tooltip content={alreadyFavorite ? t("board.weather_favorite_forget") : t("board.weather_favorite_remember")}>
            <IconButton
              size="1"
              variant="ghost"
              color={alreadyFavorite ? "yellow" : "gray"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite({
                  id: crypto.randomUUID(),
                  city_label: card.city_label,
                  country_code: card.country_code,
                  city_query: card.city_query,
                  latitude: card.latitude,
                  longitude: card.longitude,
                  timezone: card.timezone,
                  layout: card.layout,
                  style: card.style,
                });
              }}
            >
              <Star size={14} fill={alreadyFavorite ? "currentColor" : "none"} />
            </IconButton>
          </Tooltip>

          <IconButton
            variant="ghost"
            color="red"
            size="1"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            disabled={isDeleting}
            aria-label={t("board.weather_delete_aria")}
          >
            <Trash2 size={14} />
          </IconButton>
        </Flex>
      </Card>

      <PromoteDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        onConfirm={handleConfirm}
        isPending={isPromoting}
      >
        <Flex direction="column" gap="1">
          <Text as="label" size="2" weight="medium" htmlFor="promote-layout-select">
            {t("board.weather_layout_label")}
          </Text>
          <Select.Root
            value={selectedLayout}
            onValueChange={(v) => setSelectedLayout(v as WeatherLayout)}
          >
            <Select.Trigger id="promote-layout-select" style={{ width: "100%" }} />
            <Select.Content>
              {WEATHER_LAYOUTS.map((l) => (
                <Select.Item key={l} value={l}>{t(`weather.layout.${l}`)}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </PromoteDialog>
    </Box>
  );
}

function cityLabel(r: GeocodeResult): string {
  const parts = [r.name];
  if (r.admin1 && r.admin1 !== r.name) parts.push(r.admin1);
  if (r.country) parts.push(r.country);
  return parts.join(", ");
}
